/**
 * SkillPicker — shared canonical-skill picker (design "C": search + browse-by-domain).
 *
 * A presentational, opaque popover/panel for picking canonical `platform_skills`.
 * The parent owns persistence; this component only reports adds/removes.
 *
 * Used by both the Chronicle Subjects editor (DEV-1396) and the Profile
 * Capability Registry (DEV-1378) so the two surfaces share vocabulary and
 * interaction — the consistent-grammar principle.
 *
 * Behaviour:
 *   - Search box at top; type-to-filter across all canonical skills by displayed
 *     label (case-insensitive), filtered client-side.
 *   - Browse mode (search empty): skills grouped under localized domain headers.
 *   - Alphabetized everywhere via `localeCompare` under the ACTIVE i18n language —
 *     sorted on the label the USER SEES, so it stays correct once translated
 *     labels arrive. Domain groups are ordered by their localized domain label too.
 *   - No-match empty state on a search that yields nothing.
 *   - Already-selected skills render disabled/checked in the list.
 *
 * Data: fetches ALL canonical skills once via `fetchPlatformSkills({ canonicalOnly:true })`
 * and groups/sorts/filters entirely client-side.
 *
 * NOTE (flagged): the `/api/platform-skills` endpoint caps results at 100 rows
 * (`.Take(100)` in RoleDefinitionsController). Today the canonical seed is ~49
 * skills so browse mode is complete, but if the canonical set ever exceeds 100
 * this picker would silently miss skills. Backend fix is out of scope here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchPlatformSkills, type PlatformSkillResponse } from '../../api/projectRoles';
import styles from './SkillPicker.module.css';

/** The five fixed domain strings, in a stable declaration order (used only as a
 *  fallback tiebreaker; groups are ordered by their localized label at runtime). */
const KNOWN_DOMAINS = ['environmental', 'technical', 'social', 'administrative', 'trade'] as const;

export interface SkillPickerSelection {
  skillSlug: string;
  label: string;
}

export interface SkillPickerProps {
  /** Currently-selected skills (rendered disabled/checked in the list). */
  selected: SkillPickerSelection[];
  /** Called when the user picks a skill not already selected. */
  onAdd: (skill: SkillPickerSelection) => void;
  /** Called when the user removes an already-selected skill from within the list. */
  onRemove: (slug: string) => void;
  /** Disables all interaction (e.g. while the parent is persisting). */
  disabled?: boolean;
}

interface DomainGroup {
  domain: string;
  domainLabel: string;
  skills: PlatformSkillResponse[];
}

export default function SkillPicker({ selected, onAdd, onRemove, disabled }: SkillPickerProps) {
  const { t, i18n } = useAppTranslation('common');
  const lang = i18n?.language || 'en';

  const [all, setAll] = useState<PlatformSkillResponse[]>([]);
  const [query, setQuery] = useState('');

  // ── Load the full canonical set once. ──
  useEffect(() => {
    let cancelled = false;
    fetchPlatformSkills({ canonicalOnly: true })
      .then((data) => { if (!cancelled) setAll(data); })
      .catch(() => { if (!cancelled) setAll([]); });
    return () => { cancelled = true; };
  }, []);

  const selectedSlugs = useMemo(
    () => new Set(selected.map((s) => s.skillSlug.toLowerCase())),
    [selected],
  );

  /** Localized label for a domain (falls back to "other" then the raw value). */
  const domainLabel = useCallback((domain: string): string => {
    const known = (KNOWN_DOMAINS as readonly string[]).includes(domain);
    return known ? t(`skillPicker.domain.${domain}`) : (t('skillPicker.domain.other') || domain);
  }, [t]);

  /** Locale-aware label comparator under the active language. */
  const byLabel = useCallback(
    (a: PlatformSkillResponse, b: PlatformSkillResponse) =>
      a.label.localeCompare(b.label, lang, { sensitivity: 'base' }),
    [lang],
  );

  const trimmed = query.trim().toLowerCase();

  // ── Flat, alphabetized search results (label match, case-insensitive). ──
  const searchResults = useMemo(() => {
    if (!trimmed) return [];
    return all
      .filter((s) => s.label.toLowerCase().includes(trimmed))
      .sort(byLabel);
  }, [all, trimmed, byLabel]);

  // ── Browse mode: skills grouped by domain, groups ordered by localized label,
  //    skills within each group alphabetized. ──
  const groups: DomainGroup[] = useMemo(() => {
    const map = new Map<string, PlatformSkillResponse[]>();
    for (const s of all) {
      const key = s.domain || 'other';
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    const out: DomainGroup[] = Array.from(map.entries()).map(([domain, skills]) => ({
      domain,
      domainLabel: domainLabel(domain),
      skills: [...skills].sort(byLabel),
    }));
    out.sort((a, b) => a.domainLabel.localeCompare(b.domainLabel, lang, { sensitivity: 'base' }));
    return out;
  }, [all, domainLabel, byLabel, lang]);

  const handlePick = useCallback((skill: PlatformSkillResponse) => {
    if (disabled) return;
    const isSelected = selectedSlugs.has(skill.slug.toLowerCase());
    if (isSelected) onRemove(skill.slug);
    else onAdd({ skillSlug: skill.slug, label: skill.label });
  }, [disabled, selectedSlugs, onAdd, onRemove]);

  const renderRow = (s: PlatformSkillResponse) => {
    const isSelected = selectedSlugs.has(s.slug.toLowerCase());
    return (
      <li key={s.id} role="none">
        <button
          type="button"
          role="option"
          aria-selected={isSelected}
          disabled={disabled}
          onMouseDown={(e) => { e.preventDefault(); handlePick(s); }}
          className={`${styles.row} ${isSelected ? styles.rowSelected : ''}`}
        >
          <span className={styles.rowCheck} aria-hidden="true">{isSelected ? '✓' : ''}</span>
          <span className={styles.rowLabel}>{s.label}</span>
          <span className={styles.rowMeta}>
            {isSelected ? t('skillPicker.alreadyAdded') : domainLabel(s.domain)}
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className={styles.panel}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('skillPicker.searchPlaceholder')}
        aria-label={t('skillPicker.searchPlaceholder')}
        disabled={disabled}
        className={styles.search}
      />

      <div className={styles.body} role="listbox" aria-label={t('skillPicker.browseTitle')}>
        {trimmed ? (
          searchResults.length === 0 ? (
            <div className={styles.noMatch}>{t('skillPicker.noMatch')}</div>
          ) : (
            <ul className={styles.list}>
              {searchResults.map(renderRow)}
            </ul>
          )
        ) : (
          groups.map((g) => (
            <div key={g.domain} className={styles.group}>
              <div className={styles.groupHeader}>{g.domainLabel}</div>
              <ul className={styles.list}>
                {g.skills.map(renderRow)}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
